import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';

import { belowUpperBound, isInBound } from 'utils/validators';

import { VARIANT } from 'constants/inputTypes';

import InputGroupComponent from 'components/groups/InputGroup';
import TextareaGroupComponent from 'components/groups/TextareaGroup';
import { ReasonCheckbox } from 'shared/Checkbox';

import { Icons } from 'shared/Checkbox';

import StepConfirmationOverlay, { StepConfirmationField } from 'shared/StepConfirmation';

import useStores from 'utils/useStores';
import SpaceBetween from 'components/elements/SpaceBetween';
import { ReasonGet } from 'types/model/reason';
import { PhysicalAnalysisFailures } from 'types/model/IPhysicalAnalysisFailure';

import FailureInfoTooltip from './FailureInfoTooltip';

import {
  COMMIT_CONFIRM,
  COMMIT_DRAFT,
  FIELD_STEP,
  HIDDEN_FIELDS,
  STEP_BASIS,
  STEP_COMPLETED,
  STEP_DEFECTS,
  STEP_SIEVES,
} from 'stores/physicalAnalysisSteps';

export interface Validation {
  max: string;
  // PLA-1077: el asistente de compra de lotes agrega piso, unidad y si la violacion
  // admite razon de aceptacion. Las muestras sueltas siguen mandando solo `max`.
  min?: string;
  unit?: string;
  overridable?: boolean;
}

export type Validations = { [key: string]: Validation };

interface PhysicalAnalysisProps {
  reasons: ReasonGet[];
  validations: Validations;
  // PLA-1077. Por defecto false: sin esto el componente renderiza el formulario de una
  // sola pagina, tal como lo consume la pantalla de muestras sueltas.
  wizard?: boolean;
  identifier?: string;
  // PLA-1077: parametros incumplidos, calculados en el servidor. En la pantalla de cierre
  // los campos son de solo lectura, asi que los validadores del navegador (que corren al
  // escribir) no se disparan y la pantalla se veria como si todo estuviera bien.
  failures?: string[];
  // PLA-1077 v2: el porque de cada falla (PhysicalAnalysis#failure_details), con las
  // mismas claves que `failures`. Se explica con un (i) junto al campo en el cierre.
  failureDetails?: PhysicalAnalysisFailures;
}

const GRID_STYLE = { gridTemplateColumns: 'repeat(auto-fill, minmax(9em, 1fr))' };

const PhysicalAnalysisComponent = ({
  reasons,
  validations,
  wizard = false,
  identifier = '',
  failures = [],
  failureDetails = {},
}: PhysicalAnalysisProps) => {
  const { overlayStore, physicalAnalysisStore } = useStores();
  // Arranca con el veredicto del servidor: de ahi salen el check de Rechazado y el
  // resaltado de los campos que se pasaron.
  const [errors, setErrors] = useState<string[]>(failures);
  const [confirming, setConfirming] = useState(false);
  const commitTypeRef = useRef<HTMLInputElement>(null);

  const currentStep = physicalAnalysisStore.currentStep;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, setter: any) => {
    setter(e.target.value);
  };

  // Un campo solo se puede escribir en su propio paso. Los pasos ya confirmados van
  // readOnly (NO disabled: un input disabled no lo envia el navegador y perderiamos sus
  // valores en el POST).
  const isEditable = (field: string): boolean => {
    if (!wizard) {
      return true;
    }

    // PLA-1077 v1.1: ripio y caracol los pone el servidor (en 0); nunca se digitan.
    if (HIDDEN_FIELDS.includes(field)) {
      return false;
    }

    return FIELD_STEP[field] === currentStep;
  };

  const belowUpperBoundValidator = (
    standardValue: string,
    currentValue: string,
    inputValue?: string
  ) => {
    const validation = validations[standardValue];
    let validator = true;

    if (validation && inputValue !== '') {
      const value = parseFloat(physicalAnalysisStore[currentValue]);

      if (validation.max !== null && validation.max !== undefined) {
        validator = belowUpperBound(value, parseFloat(validation.max));
      }

      // PLA-1077: piso, cuando el parametro lo define. `min` 0 significa "mayor que
      // cero" (notas 5 y 11 del ticket).
      if (validator && validation.min !== null && validation.min !== undefined) {
        validator = value > parseFloat(validation.min);
      }
    }

    if (!validator) {
      if (!errors.includes(standardValue)) {
        setErrors([...errors, standardValue]);
      }
    } else {
      setErrors(errors.filter((error) => error !== standardValue));
    }
    return validator;
  };

  useEffect(() => {
    overlayStore.setValid(errors.length === 0);
  }, [errors]);

  useEffect(() => {
    if (physicalAnalysisStore.acceptingReasonId) {
      overlayStore.setReasonId(physicalAnalysisStore.acceptingReasonId);
      overlayStore.setValid(false);
      overlayStore.setOverrideValidation(true);
    }
  }, [physicalAnalysisStore.acceptingReasonId]);

  useEffect(() => {
    if (overlayStore.isValid && overlayStore.overrideValidation) {
      overlayStore.setOverrideValidation(false);
    }
  }, [overlayStore.isValid]);

  useEffect(() => {
    const validFactor14 =
      belowUpperBound(
        parseFloat(physicalAnalysisStore.factor14) || 0,
        parseFloat(validations.factor14?.max)
      ) || !validations.factor14?.max;

    const validFactor15 =
      belowUpperBound(
        parseFloat(physicalAnalysisStore.factor15),
        parseFloat(validations.factor15?.max)
      ) || !validations.factor15?.max;

    let tmpErrors = errors;

    if (!validFactor14) {
      if (!errors.includes('factor14')) {
        tmpErrors = [...errors, 'factor14'];
      }
    } else {
      if (errors.includes('factor14')) {
        tmpErrors = tmpErrors.filter((error) => error !== 'factor14');
      }
    }

    if (!validFactor15) {
      if (!errors.includes('factor15')) {
        tmpErrors = [...tmpErrors, 'factor15'];
      }
    } else {
      if (errors.includes('factor15')) {
        tmpErrors = tmpErrors.filter((error) => error !== 'factor15');
      }
    }

    setErrors(tmpErrors);
  }, [physicalAnalysisStore.sieve14, physicalAnalysisStore.sieve15]);

  const greenValidator = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Fuera del asistente el rango sigue quemado en 1..300. Dentro, sale de la tabla
    // de parametros como cualquier otro campo.
    const validation = wizard ? validations.green : null;
    const min = validation?.min !== undefined ? parseFloat(validation.min) : 1;
    const max = validation?.max !== undefined ? parseFloat(validation.max) : 300;

    const validator = isInBound(parseFloat(e.target.value), min, max);

    if (!validator) {
      if (!errors.includes('green')) {
        setErrors([...errors, 'green']);
      }
    } else {
      setErrors(errors.filter((error) => error !== 'green'));
    }

    return validator;
  };

  // La banda aceptada para la suma llega como un limite mas (`sieveSumPercentage`), asi
  // que se configura por origen desde Basedata. Sin fila configurada, igualdad exacta.
  // Inclusiva en los dos extremos: el 100% exacto es el caso perfecto.
  const sieveSumValidator = () => {
    if (!wizard) {
      return physicalAnalysisStore.sumMatchesGreen;
    }

    const green = parseFloat(physicalAnalysisStore.green);
    const sum = parseFloat(physicalAnalysisStore.sieveSum);

    if (isNaN(green) || isNaN(sum) || green === 0) {
      return false;
    }

    const band = validations.sieveSumPercentage;
    const percentage = (sum / green) * 100;
    const min = band?.min == null ? 100 : parseFloat(band.min);
    const max = band?.max == null ? 100 : parseFloat(band.max);

    return percentage >= min && percentage <= max;
  };

  // PLA-1077 v2: solo en el cierre. En los pasos 1-3 el rojo lo pone el navegador al
  // digitar y ahi, a proposito, no se muestra el porcentaje (ver decimalField).
  const failureInfo = (key: string) =>
    wizard && currentStep === STEP_COMPLETED && failureDetails[key] ? (
      <FailureInfoTooltip detail={failureDetails[key]} />
    ) : undefined;

  // --- Definicion de cada campo, compartida por ambos layouts -------------------------

  const decimalField = (
    field: string,
    label: string,
    storeValue: string,
    storePercentage: string | null,
    validationKey: string,
    setter: any
  ) => (
    <InputGroupComponent
      label={I18n.translate(`attributes.${label}`)}
      name={field}
      // PLA-1077: dentro del asistente no se muestra el porcentaje debajo del campo. Es
      // el numero contra el que se compara el limite, y verlo mientras se digita invita
      // a acomodar los gramos hasta que quede por debajo.
      infoname={!wizard && storePercentage ? `${field}_percentage` : undefined}
      variant={VARIANT.DECIMAL}
      append='gr'
      value={physicalAnalysisStore[storeValue] || ''}
      placeholder='0.00'
      info={
        !wizard && storePercentage
          ? physicalAnalysisStore[storePercentage]
            ? `${physicalAnalysisStore[storePercentage]}%`
            : '--'
          : undefined
      }
      validator={(e) =>
        belowUpperBoundValidator(
          validationKey,
          storePercentage || storeValue,
          e.target.value
        )
      }
      onChange={(e) => handleInputChange(e, physicalAnalysisStore[setter])}
      readOnly={!isEditable(field)}
      externalValid={!failures.includes(validationKey)}
      labelAddon={failureInfo(validationKey)}
      required={wizard || FIELD_STEP[field] === STEP_BASIS}
    />
  );

  const greenField = () => (
    <InputGroupComponent
      label={I18n.translate('attributes.green')}
      name='green'
      variant={VARIANT.DECIMAL}
      append='gr'
      value={physicalAnalysisStore.green || ''}
      placeholder='0.00'
      validator={greenValidator}
      onChange={(e) => handleInputChange(e, physicalAnalysisStore.setGreen)}
      readOnly={!isEditable('green')}
      externalValid={!failures.includes('green')}
      labelAddon={failureInfo('green')}
      required={true}
    />
  );

  const mermaField = () => (
    <InputGroupComponent
      label={I18n.translate('attributes.merma')}
      name='merma'
      append='%'
      value={physicalAnalysisStore.mermaPercentage}
      validator={() => belowUpperBoundValidator('mermaPercentage', 'mermaPercentage')}
      externalValid={!failures.includes('mermaPercentage')}
      labelAddon={failureInfo('mermaPercentage')}
      readOnly
      disabled
    />
  );

  const sieveSumField = () => (
    <InputGroupComponent
      label={I18n.translate('attributes.sieve_sum')}
      append='Σ'
      value={physicalAnalysisStore.sieveSum}
      validator={() => sieveSumValidator()}
      readOnly
      disabled
    />
  );

  const densityField = () => (
    <InputGroupComponent
      label={I18n.translate('attributes.density')}
      name='density'
      variant={VARIANT.DECIMAL}
      placeholder='0.00'
      value={physicalAnalysisStore.density || ''}
      onChange={(e) => handleInputChange(e, physicalAnalysisStore.setDensity)}
      readOnly={!isEditable('density')}
      externalValid={!failures.includes('density')}
      labelAddon={failureInfo('density')}
      required={wizard}
    />
  );

  const calculatedFields = () => (
    <>
      <InputGroupComponent
        label={I18n.translate('attributes.factor_14')}
        name='factor_14'
        validator={() => belowUpperBoundValidator('factor14', 'factor14')}
        value={physicalAnalysisStore.factor14}
        externalValid={!failures.includes('factor14')}
        labelAddon={failureInfo('factor14')}
        readOnly
      />
      <InputGroupComponent
        label={I18n.translate('attributes.factor_15')}
        name='factor_15'
        validator={() => belowUpperBoundValidator('factor15', 'factor15')}
        value={physicalAnalysisStore.factor15}
        externalValid={!failures.includes('factor15')}
        labelAddon={failureInfo('factor15')}
        readOnly
      />
      <InputGroupComponent
        label={I18n.translate('attributes.yield_14')}
        name='yield_14'
        value={physicalAnalysisStore.yield14}
        readOnly
      />
      <InputGroupComponent
        label={I18n.translate('attributes.yield_15')}
        name='yield_15'
        value={physicalAnalysisStore.yield15}
        readOnly
      />
    </>
  );

  const sieve0Field = () =>
    decimalField(
      'sieve_0',
      'sieve_0',
      'sieve0',
      'sieve0Percentage',
      'sieve0Percentage',
      'setSieve0'
    );
  const sieve12Field = () =>
    decimalField(
      'sieve_12',
      'sieve_12',
      'sieve12',
      'sieve12Percentage',
      'sieve12Percentage',
      'setSieve12'
    );
  const sieve14Field = () =>
    decimalField(
      'sieve_14',
      'sieve_14',
      'sieve14',
      'sieve14Percentage',
      'sieve14Percentage',
      'setSieve14'
    );
  const sieve15Field = () =>
    decimalField(
      'sieve_15',
      'sieve_15_18',
      'sieve15',
      'sieve15Percentage',
      'sieve15Percentage',
      'setSieve15'
    );

  const defectFields = () => (
    <>
      {decimalField(
        'group_1',
        'group_1',
        'group1',
        'group1Percentage',
        'group1Percentage',
        'setGroup1'
      )}
      {decimalField(
        'group_2',
        'group_2',
        'group2',
        'group2Percentage',
        'group2Percentage',
        'setGroup2'
      )}
      {decimalField(
        'broca',
        'broca',
        'broca',
        'brocaPercentage',
        'brocaPercentage',
        'setBroca'
      )}
      {decimalField(
        'paloteo',
        'paloteo',
        'paloteo',
        'paloteoPercentage',
        'paloteoPercentage',
        'setPaloteo'
      )}
      {!physicalAnalysisStore.isHiddenField('ripio') &&
        decimalField(
          'ripio',
          'ripio',
          'ripio',
          'ripioPercentage',
          'ripioPercentage',
          'setRipio'
        )}
      {decimalField(
        'guayabas',
        'guayabas',
        'guayabas',
        'guayabasPercentage',
        'guayabasPercentage',
        'setGuayabas'
      )}
      {!physicalAnalysisStore.isHiddenField('caracol') &&
        decimalField(
          'caracol',
          'caracol',
          'caracol',
          'caracolPercentage',
          'caracolPercentage',
          'setCaracol'
        )}
      {densityField()}
    </>
  );

  const observationsField = () => (
    <div className='input-group'>
      <TextareaGroupComponent
        placeholder='max. 200 characters'
        data-cy='physical_analysis_observations'
        name='observations'
        id='observations'
        label={I18n.translate('attributes.observations')}
        value={physicalAnalysisStore.observations || ''}
        onChange={(e) => physicalAnalysisStore.setObservations(e.target.value)}
        readOnly={!isEditable('observations')}
      />
    </div>
  );

  // --- Botonera y confirmacion --------------------------------------------------------

  const setCommitType = (value: string) => {
    if (commitTypeRef.current) {
      commitTypeRef.current.value = value;
    }
  };

  // El <form> lo renderiza Rails, fuera de React, y el overlay vive en un portal fuera de
  // ese form: hay que enviarlo a mano. Se busca por `closest` desde el input oculto en vez
  // de por nombre: `form_for ... name: 'analysis'` NO emite el atributo name (para eso
  // habria que pasarlo dentro de `html:`), asi que buscarlo por nombre devuelve null.
  const submitAnalysisForm = () => {
    const form = commitTypeRef.current?.closest('form');
    if (!form) {
      return;
    }

    // requestSubmit respeta la validacion HTML5; submit la saltea.
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  };

  const confirmationFields = (): StepConfirmationField[] => {
    const byStep: { [step: number]: [string, string, string?][] } = {
      [STEP_BASIS]: [
        ['green', physicalAnalysisStore.green, 'gr'],
        ['sieve_0', physicalAnalysisStore.sieve0, 'gr'],
        ['sieve_12', physicalAnalysisStore.sieve12, 'gr'],
      ],
      [STEP_DEFECTS]: [
        ['group_1', physicalAnalysisStore.group1, 'gr'],
        ['group_2', physicalAnalysisStore.group2, 'gr'],
        ['broca', physicalAnalysisStore.broca, 'gr'],
        ['paloteo', physicalAnalysisStore.paloteo, 'gr'],
        ['ripio', physicalAnalysisStore.ripio, 'gr'],
        ['guayabas', physicalAnalysisStore.guayabas, 'gr'],
        ['caracol', physicalAnalysisStore.caracol, 'gr'],
        ['density', physicalAnalysisStore.density],
      ],
      [STEP_SIEVES]: [
        ['sieve_14', physicalAnalysisStore.sieve14, 'gr'],
        ['sieve_15_18', physicalAnalysisStore.sieve15, 'gr'],
      ],
      // En el cierre no se confirman campos digitados sino el RESULTADO del analisis:
      // es lo que el analista tiene que mirar antes de aceptar o rechazar el lote.
      [STEP_COMPLETED]: [
        ['factor_14', physicalAnalysisStore.factor14],
        ['factor_15', physicalAnalysisStore.factor15],
        ['yield_14', physicalAnalysisStore.yield14],
        ['yield_15', physicalAnalysisStore.yield15],
      ],
    };

    // PLA-1077 v1.1: ripio y caracol tampoco se muestran al confirmar el paso.
    return (byStep[currentStep] || [])
      .filter(([label]) => !physicalAnalysisStore.isHiddenField(label))
      .map(([label, value, suffix]) => ({
        label: I18n.translate(`attributes.${label}`),
        value: value || '',
        suffix,
      }));
  };

  const wizardButtons = () => (
    <div className='fieldset'>
      <input
        type='hidden'
        name='commit_type'
        ref={commitTypeRef}
        defaultValue={COMMIT_DRAFT}
      />

      <SpaceBetween>
        <button
          type='button'
          className='button button--gray'
          onClick={() => window.history.back()}
        >
          {I18n.translate('buttons.cancel')}
        </button>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type='submit'
            name='commit'
            className='button button--green'
            data-cy='save_physical_analysis_draft'
            onClick={() => setCommitType(COMMIT_DRAFT)}
          >
            {I18n.translate('buttons.save')}
          </button>

          {/* En un analisis ya cerrado solo se editan las observaciones: no hay nada
              nuevo que congelar, asi que no se vuelve a pedir la confirmacion. */}
          {!physicalAnalysisStore.isClosed && (
            <button
              type='button'
              className='button'
              data-cy='confirm_physical_analysis_step'
              disabled={currentStep === STEP_SIEVES && !sieveSumValidator()}
              onClick={() => setConfirming(true)}
            >
              {currentStep === STEP_COMPLETED
                ? I18n.translate('physical_analysis.finish')
                : I18n.translate('physical_analysis.next_step')}
            </button>
          )}
        </div>
      </SpaceBetween>
    </div>
  );

  const legacyButtons = () => (
    <div className='fieldset'>
      <SpaceBetween>
        <button
          type='button'
          className='button button--gray'
          onClick={() => window.history.back()}
        >
          {I18n.translate('buttons.cancel')}
        </button>
        <button
          type='submit'
          name='commit'
          disabled={!sieveSumValidator()}
          className='button'
          data-cy='save_physical_analysis'
          data-disable-with='Save'
        >
          {I18n.translate('buttons.save')}
        </button>
      </SpaceBetween>
    </div>
  );

  // El checkbox de rechazo solo se puede tocar en el ultimo paso, y nunca si algun
  // parametro incumplido no admite razon de aceptacion (falla dura).
  const rejectSection = () => (
    <div className='fieldset'>
      <div className='l-auto-fill-grid' style={GRID_STYLE}>
        <ReasonCheckbox
          reasons={reasons}
          name='reject_reason'
          icon={Icons.cross}
          label={I18n.translate('attributes.rejected')}
          locked={wizard && (currentStep !== STEP_COMPLETED || hasHardFailure())}
        />
      </div>
    </div>
  );

  // En el cierre manda la foto del servidor (failureDetails), no la parametrizacion
  // vigente: si un limite cambio despues de cerrar, el bloqueo sigue el de ese dia.
  const hasHardFailure = (): boolean =>
    Object.keys(failureDetails).length > 0
      ? Object.values(failureDetails).some((detail) => detail.overridable === false)
      : errors.some((key) => validations[key]?.overridable === false);

  overlayStore.setFilled(true);

  if (!wizard) {
    return (
      <>
        <div className='fieldset'>
          <div className='l-auto-fill-grid' style={GRID_STYLE}>
            {greenField()}
            {mermaField()}
            {sieveSumField()}
            <span className='empty-cell' />
            {sieve0Field()}
            {sieve12Field()}
            {sieve14Field()}
            {sieve15Field()}
            {calculatedFields()}
          </div>
        </div>
        <div className='fieldset'>
          <div className='l-auto-fill-grid' style={GRID_STYLE}>
            {defectFields()}
          </div>
        </div>
        <div className='fieldset'>
          <div
            className='l-auto-fill-grid'
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(18em, 1fr))' }}
          >
            {observationsField()}
          </div>
        </div>
        {rejectSection()}
        {legacyButtons()}
      </>
    );
  }

  return (
    <>
      <ol className='physical-analysis-steps'>
        {[STEP_BASIS, STEP_DEFECTS, STEP_SIEVES, STEP_COMPLETED].map((step) => (
          <li
            key={step}
            className={
              step < currentStep ? 'is-done' : step === currentStep ? 'is-current' : ''
            }
          >
            {I18n.translate(`physical_analysis.steps.${step}`)}
          </li>
        ))}
      </ol>

      {currentStep === STEP_BASIS && (
        <div className='fieldset'>
          <div className='l-auto-fill-grid' style={GRID_STYLE}>
            {greenField()}
            {mermaField()}
            {sieve0Field()}
            {sieve12Field()}
          </div>
        </div>
      )}

      {currentStep === STEP_DEFECTS && (
        <div className='fieldset'>
          <div className='l-auto-fill-grid' style={GRID_STYLE}>
            {defectFields()}
          </div>
        </div>
      )}

      {currentStep === STEP_SIEVES && (
        <div className='fieldset'>
          <div className='l-auto-fill-grid' style={GRID_STYLE}>
            {sieve14Field()}
            {sieve15Field()}
            {sieveSumField()}
          </div>
        </div>
      )}

      {/* Cierre: todo lo cargado, ya congelado, mas los calculados, las observaciones
          y la decision de aceptar o rechazar. */}
      {currentStep === STEP_COMPLETED && (
        <>
          <div className='fieldset'>
            <div className='l-auto-fill-grid' style={GRID_STYLE}>
              {greenField()}
              {mermaField()}
              {sieve0Field()}
              {sieve12Field()}
              {sieve14Field()}
              {sieve15Field()}
              {sieveSumField()}
              <span className='empty-cell' />
              {calculatedFields()}
            </div>
          </div>

          <div className='fieldset'>
            <div className='l-auto-fill-grid' style={GRID_STYLE}>
              {defectFields()}
            </div>
          </div>

          <div className='fieldset'>
            <div
              className='l-auto-fill-grid'
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(18em, 1fr))' }}
            >
              {observationsField()}
            </div>
          </div>

          {rejectSection()}
        </>
      )}

      {wizardButtons()}

      {confirming && (
        <StepConfirmationOverlay
          identifier={identifier}
          stepLabel={I18n.translate(`physical_analysis.steps.${currentStep}`)}
          fields={confirmationFields()}
          onCancelClick={() => setConfirming(false)}
          onConfirmClick={() => {
            setCommitType(COMMIT_CONFIRM);
            submitAnalysisForm();
            setConfirming(false);
          }}
        />
      )}
    </>
  );
};

export default observer(PhysicalAnalysisComponent);
